import { get, off, ref, remove, set, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "./auth";
import { LaunchError } from "../utils/errors";
import { buildCompetitorAdFavoritePayload } from "../services/metaAdLibrary";

export const COMPETITOR_FAVORITES_NODE_KEY = "favorite";

const getCompetitorAdsCachePath = ({ advertiserID }) =>
  `advertisersCompetitors/${advertiserID}`;

const getCompetitorAdFavoritesPath = ({ advertiserID }) =>
  `${getCompetitorAdsCachePath({ advertiserID })}/${COMPETITOR_FAVORITES_NODE_KEY}`;

const getCompetitorAdFavoriteItemPath = ({ advertiserID, pageId, libraryID }) =>
  `${getCompetitorAdFavoritesPath({ advertiserID })}/${pageId}/${libraryID}`;

const getCompetitorAdCacheItemPath = ({ advertiserID, pageId, libraryID }) =>
  `${getCompetitorAdsCachePath({ advertiserID })}/${pageId}/ads/${libraryID}`;

export const startInvalidateCompetitorAdsCache = () => async (dispatch) => {
  try {
    const { userID } = dispatch(getPathDatabaseByRole());

    if (!userID) {
      return { ok: false };
    }

    const cachePath = getCompetitorAdsCachePath({ advertiserID: userID });
    const snapshot = await get(ref(db, cachePath));
    const entries = snapshot.val() || {};
    const updates = {};

    Object.keys(entries).forEach((key) => {
      if (key !== COMPETITOR_FAVORITES_NODE_KEY) {
        updates[`${cachePath}/${key}`] = null;
      }
    });

    if (Object.keys(updates).length === 0) {
      return { ok: true };
    }

    await update(ref(db), updates);

    return { ok: true };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startInvalidateCompetitorAdsCache",
        tags: { action: "competitorAdsCache" },
        level: "error",
      })
    );
    return { ok: false };
  }
};

export const startGetCompetitorAdsCache = () => async (dispatch) => {
  try {
    const { userID } = dispatch(getPathDatabaseByRole());

    if (!userID) {
      return { ok: false, data: null };
    }

    const snapshot = await get(
      ref(db, getCompetitorAdsCachePath({ advertiserID: userID }))
    );

    return {
      ok: true,
      data: snapshot.val() || null,
    };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetCompetitorAdsCache",
        tags: { action: "competitorAdsCache" },
        level: "error",
      })
    );
    return { ok: false };
  }
};

export const startListenerCompetitorAdsCache =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID) {
        onUpdatedDataCallback(null);
        return false;
      }

      const dbRef = ref(
        db,
        getCompetitorAdsCachePath({ advertiserID: userID })
      );
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || null);
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCompetitorAdsCache",
          tags: { action: "competitorAdsCache" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCompetitorAdsCache =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) {
      return;
    }

    await off(listenerRef);
  };

export const startListenerCompetitorAdFavorites =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID) {
        onUpdatedDataCallback(null);
        return false;
      }

      const dbRef = ref(
        db,
        getCompetitorAdFavoritesPath({ advertiserID: userID })
      );
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || null);
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCompetitorAdFavorites",
          tags: { action: "competitorAdFavorites" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCompetitorAdFavorites =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) {
      return;
    }

    await off(listenerRef);
  };

export const startSetCompetitorAdFavorite =
  ({ pageId, libraryID, ad }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID || !pageId || !libraryID || !ad?.url) {
        return { ok: false };
      }

      const payload = buildCompetitorAdFavoritePayload({
        pageId,
        libraryID,
        ad,
      });
      const favoriteRef = ref(
        db,
        getCompetitorAdFavoriteItemPath({
          advertiserID: userID,
          pageId,
          libraryID,
        })
      );
      const cacheRef = ref(
        db,
        getCompetitorAdCacheItemPath({
          advertiserID: userID,
          pageId,
          libraryID,
        })
      );

      await Promise.all([
        set(favoriteRef, payload),
        update(cacheRef, { isFavorite: true }),
      ]);

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSetCompetitorAdFavorite",
          context: { pageId, libraryID },
          tags: { action: "competitorAdFavorites" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startRemoveCompetitorAdFavorite =
  ({ pageId, libraryID }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID || !pageId || !libraryID) {
        return { ok: false };
      }

      const favoriteRef = ref(
        db,
        getCompetitorAdFavoriteItemPath({
          advertiserID: userID,
          pageId,
          libraryID,
        })
      );
      const cacheRef = ref(
        db,
        getCompetitorAdCacheItemPath({
          advertiserID: userID,
          pageId,
          libraryID,
        })
      );

      await Promise.all([
        remove(favoriteRef),
        update(cacheRef, { isFavorite: null }),
      ]);

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRemoveCompetitorAdFavorite",
          context: { pageId, libraryID },
          tags: { action: "competitorAdFavorites" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startGetCompetitorAdFavorites = () => async (dispatch) => {
  try {
    const { userID } = dispatch(getPathDatabaseByRole());

    if (!userID) {
      return { ok: false, data: null };
    }

    const snapshot = await get(
      ref(db, getCompetitorAdFavoritesPath({ advertiserID: userID }))
    );

    return {
      ok: true,
      data: snapshot.val() || null,
    };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetCompetitorAdFavorites",
        tags: { action: "competitorAdFavorites" },
        level: "error",
      })
    );
    return { ok: false };
  }
};
